import { useMemo } from "react"

import { Table } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import EmptyComponent from "../../../../Components/Empty"
import { getDashboardProgressRowProps } from "../../../DashboardPage/components/Table/columns"
import SummaryRows from "../../../DeflectAnalyticsPage/components/SummaryRows/SummaryRows"
import { granularityTag } from "../../charts/axis"
import { SERIES_COLORS } from "../../charts/palette"
import StackedBarChart from "../../charts/StackedBarChart"
import TrendLineChart from "../../charts/TrendLineChart"
import ChangeBadge from "../../components/Badges/ChangeBadge"
import GroupMarker from "../../components/cells/GroupMarker"
import { numberCell } from "../../components/cells/numberCell"
import ProgramTag from "../../components/cells/ProgramTag"
import KpiStrip from "../../components/KpiStrip/KpiStrip"
import MetricCard from "../../components/MetricCard/MetricCard"
import { Panel, PanelRow } from "../../components/Panel/Panel"
import { CHANNEL_OPTIONS, PROGRAM_OPTIONS } from "../../constants"
import { useCardData } from "../../hooks/useCardData"
import { getPercentChange, GOOD_DIRECTION } from "../../utils/change"
import { formatCount, formatShare, toShare } from "../../utils/formats"

import rowLines from "../../components/RowLines/rowLines.module.less"

// The card's Tooltip terms, verbatim from the spec (§4 Lookup volume).
const INFO = [
  {
    name: "Total lookups",
    formula:
      "Number of lookups in the period: matched and not matched, with and without a merchant, on every channel"
  },
  {
    name: "Without merchant",
    formula: "Lookups in the period that are not attached to a merchant"
  },
  { name: "Daily average", formula: "Total lookups ÷ number of days in the period" },
  {
    name: "Change",
    formula: 'Change in % against the comparison period. When the comparison value is 0 — "n/a"'
  },
  {
    name: "By channel",
    formula:
      "Lookups in each interval, separately for each channel: banking app, call center, Mastercom, other"
  },
  { name: "By program", formula: "Lookups in each interval, separately for CC and OI" },
  { name: "Matrix: share", formula: "Count in the row ÷ all lookups in the period × 100" },
  {
    name: "Matrix: % without merchant",
    formula: "Lookups without a merchant in the row ÷ count in the row × 100"
  }
]

const CHANNEL_NAMES = Object.fromEntries(CHANNEL_OPTIONS.map((option) => [option.id, option.name]))

// Lookup volume (DAA-4-01 … DAA-4-06): how many times banks asked for a receipt — the KPIs, the
// lookups per channel group (stacked) and per program over time, and the program × channel matrix.
const LookupVolumeCard = () => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const { data, loading } = useCardData("lookupVolume")
  const kpis = data?.kpis
  const comparison = data?.comparison

  const tiles = useMemo(() => {
    // Change badges only while comparing (DAA-2-22).
    const badge = (value, previous, goodDirection) =>
      comparison ? <ChangeBadge change={getPercentChange(value, previous, goodDirection)} /> : null

    return [
      {
        key: "total",
        label: t("Total lookups"),
        value: kpis?.total,
        badge: badge(kpis?.total, comparison?.total, GOOD_DIRECTION.UP),
        secondary: comparison ? `vs ${formatCount(comparison.total)} ${t("prev. period")}` : null
      },
      {
        key: "withoutMerchant",
        label: t("Without merchant"),
        value: kpis?.without_merchant,
        badge: badge(kpis?.without_merchant, comparison?.without_merchant, GOOD_DIRECTION.DOWN),
        secondary: comparison
          ? `vs ${formatCount(comparison.without_merchant)} ${t("prev. period")}`
          : null,
        note: kpis
          ? `${formatShare(toShare(kpis.without_merchant, kpis.total))} ${t("of lookups")}`
          : null
      },
      {
        key: "dailyAverage",
        label: t("Daily average"),
        value: kpis ? Math.round(kpis.daily_average) : null,
        badge: badge(kpis?.daily_average, comparison?.daily_average, GOOD_DIRECTION.UP),
        secondary: comparison
          ? `vs ${formatCount(comparison.daily_average)} ${t("prev. period")}`
          : null,
        note: t("lookups per day")
      }
    ]
  }, [kpis, comparison, t])

  const channelSeries = useMemo(
    () =>
      CHANNEL_OPTIONS.filter((option) => data?.by_channel[option.id]).map((option) => ({
        key: option.id,
        label: t(option.name),
        color: SERIES_COLORS[option.id],
        data: data.by_channel[option.id]
      })),
    [data, t]
  )
  // "all" has no series of its own, so only the programs drop in.
  const programSeries = useMemo(
    () =>
      PROGRAM_OPTIONS.filter((option) => data?.by_program[option.value]).map((option) => ({
        key: option.value,
        label: option.label,
        color: SERIES_COLORS[option.value],
        data: data.by_program[option.value]
      })),
    [data]
  )

  // `percentage` is the share the platform's breakdown table draws along a row's bottom.
  const rows = useMemo(
    () =>
      (data?.matrix ?? []).map((row) => ({
        ...row,
        key: `${row.program}-${row.channel_group}`,
        percentage: row.share
      })),
    [data]
  )
  const columns = useMemo(
    () => [
      {
        title: t("Program"),
        dataIndex: "program",
        render: (program) => <ProgramTag program={program} />
      },
      {
        title: t("Channel"),
        dataIndex: "channel_group",
        render: (group) => (
          <GroupMarker color={SERIES_COLORS[group]} name={t(CHANNEL_NAMES[group])} />
        )
      },
      {
        title: t("Lookups"),
        dataIndex: "lookups",
        align: "right",
        render: numberCell(formatCount)
      },
      {
        title: t("Share"),
        dataIndex: "percentage",
        align: "right",
        render: numberCell(formatShare)
      },
      {
        title: t("Without merchant"),
        dataIndex: "without_merchant",
        align: "right",
        render: numberCell(formatCount)
      },
      {
        title: t("% w/o merchant"),
        dataIndex: "without_merchant_share",
        align: "right",
        render: numberCell(formatShare)
      }
    ],
    [t]
  )

  return (
    <MetricCard title={t("Lookup volume")} info={INFO} loading={loading}>
      <KpiStrip tiles={tiles} />
      {/* The panels keep their place until the first answer, so the page does not jump under them. */}
      <PanelRow>
        <Panel
          title={t("By channel")}
          tag={data && t(granularityTag(data.granularity, { stacked: true }))}
          span={8}
        >
          {/* A legend and a Total row even for one channel: the card's rule (DAA-4-04) over DAA-2-25's. */}
          <div style={{ height: 260 }}>
            {data && <StackedBarChart labels={data.labels} series={channelSeries} legend />}
          </div>
        </Panel>
        <Panel title={t("By program")} tag={data && t(granularityTag(data.granularity))} span={4}>
          {/* A legend even for one program, by the same rule (DAA-4-05). */}
          <div style={{ height: 260 }}>
            {data && (
              <TrendLineChart
                labels={data.labels}
                series={programSeries}
                lastIntervalIncomplete={data.last_interval_incomplete}
                // A lone program's line gets its area, as the design draws it.
                fill={programSeries.length === 1}
                legend
              />
            )}
          </div>
        </Panel>
      </PanelRow>
      <PanelRow>
        <Panel title={t("Matrix")}>
          {data && (
            <Table
              className={cls("chart-table", rowLines.rowLines)}
              dataSource={rows}
              columns={columns}
              onRow={getDashboardProgressRowProps}
              pagination={false}
              tableLayout="fixed"
              // Six columns do not fit a phone: the table scrolls sideways, as the platform's wide tables.
              scroll={isMobile ? { x: 640 } : undefined}
              showHeader={rows.length > 0}
              locale={{ emptyText: <EmptyComponent type="no_data_available" /> }}
              summary={() =>
                rows.length > 0 && (
                  <SummaryRows
                    label={t("Total")}
                    summary={kpis}
                    cells={[
                      null,
                      { align: "right", render: (summary) => formatCount(summary.total) },
                      { align: "right", render: () => "100%" },
                      {
                        align: "right",
                        render: (summary) => formatCount(summary.without_merchant)
                      },
                      {
                        align: "right",
                        render: (summary) =>
                          formatShare(toShare(summary.without_merchant, summary.total))
                      }
                    ]}
                  />
                )
              }
            />
          )}
        </Panel>
      </PanelRow>
    </MetricCard>
  )
}

export default LookupVolumeCard
